import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import api from '../../../services/api';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { TanTable } from '../../../components/TanTable';
import { useTryPermissions } from '../../../contexts/PermissionContext';
import {
  useAdditionalFieldColumns,
  useAdditionalFieldSelection,
} from '../../../hooks/useAdditionalFields';
import { FinalizeCell, FinalizeDetail, FinalizeDetailResponse } from './logic';

/**
 * Drill-down from a "Prepulls / Deliveries not finalized" cell on the LC Metrics
 * Pipeline panel: the orders that cell counted, one row each.
 *
 * Fetched rather than derived, from the same `_snapshot_scope` predicates the
 * count is built from (`sections=finalize_detail`), so the list and the number
 * above it cannot disagree. The columns are the scheduling story — who the LC is,
 * whose freight it is, what was proposed, and what CMS actually holds — and any
 * order field can be added beside them from the grid's picker.
 */

type Row = FinalizeDetail & Record<string, unknown>;

/** Registry fields the built-in columns already render, so the picker doesn't
 *  offer them twice and each takes the registry's name for the field. */
const FINALIZE_COVERED_FIELDS = {
  pro: ['pronumber'],
  customer: ['customername'],
  delivery_location: ['destname'],
  cms_delivery_appt: ['d_destapptdate'],
  available: ['availablegoodtogodatetime'],
  container: ['container'],
  movetype: ['movetype'],
} as const;

const SETTINGS_KEY = 'lc-metrics-finalize-detail';

// 'YYYY-MM-DD' -> 'M/D/YY' (string-wise, no UTC shift).
const fmtDate = (s?: string): string => {
  if (!s) return '-';
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return s;
  return `${m}/${d}/${String(y).slice(2)}`;
};

/** 'PENDING_CUSTOMER' -> 'Pending Customer'. */
const fmtStatus = (s: string): string =>
  (s || '').split('_').filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
    .join(' ') || '-';

interface Props {
  open: boolean;
  cell: FinalizeCell | null;
  /** The page's date range. The pipeline is point-in-time so it does not filter
   *  these rows; the endpoint requires it and the added-columns lookup rides it. */
  range: [string, string] | null;
  /** Customer key statuses the page is filtered to — the snapshot counts honour
   *  them, so the drill-down must too or it lists orders the cell never counted. */
  keyStatuses: string[];
  lcs?: string[];
  onClose: () => void;
  onProClick: (pro: number) => void;
}

const FinalizeDetailModal: React.FC<Props> = ({ open, cell, range, keyStatuses, lcs = [], onClose, onProClick }) => {
  const perms = useTryPermissions();
  const canViewPro = perms ? perms.hasAccess('/pro/:pronumber') : true;

  const [rows, setRows] = useState<Row[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Added order columns. One selection for both matrices: a user who wants Last
  // Free Day beside an unfinalized prepull wants it beside an unfinalized delivery
  // too — the two tables are the same orders at different stages.
  const fieldSelection = useAdditionalFieldSelection(open ? SETTINGS_KEY : null);
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const { ready } = fieldSelection;

  const keyStatusKey = keyStatuses.join(',');
  const lcsKey = lcs.join(',');
  const cellKey = cell ? `${cell.kind}|${cell.bucket ?? ''}|${cell.lc ?? ''}` : '';
  /** Which cell the rows currently in state belong to. */
  const loadedCellRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!open || !cell || !range) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    // Clicking a different cell must drop the previous one's rows BEFORE the new
    // request lands. The header's "Open N pros in search" is derived from them, so
    // leaving them up means it spends the whole load offering the pros of the cell
    // you just navigated away from — beside a title that already names the new one.
    // Only on a cell CHANGE: reloading for an added column keeps the same orders,
    // so blanking there would flicker the link away for no reason.
    if (loadedCellRef.current !== cellKey) {
      loadedCellRef.current = cellKey;
      setRows([]);
      setTruncated(false);
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/reporting/dispatcher-metrics', {
        params: {
          start_date: range[0],
          end_date: range[1],
          sections: 'finalize_detail',
          background: 'true',   // the page's own request already records the run
          kind: cell.kind,
          ...(cell.bucket ? { bucket: cell.bucket } : {}),
          ...(cell.lc ? { lc: cell.lc } : {}),
          ...(keyStatusKey ? { key_status: keyStatusKey } : {}),
          ...(lcsKey ? { lcs: lcsKey } : {}),
          ...(additionalFieldsKey ? { additional_fields: additionalFieldsKey } : {}),
        },
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      const payload: FinalizeDetailResponse | undefined = res.data?.finalize_detail;
      setRows((payload?.rows ?? []) as Row[]);
      setTruncated(!!payload?.truncated);
    } catch (e: unknown) {
      const err = e as { name?: string; code?: string };
      if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') setError('Failed to load');
    } finally {
      if (abortRef.current === controller) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cellKey, range, keyStatusKey, lcsKey, additionalFieldsKey]);

  // Held until `ready`, or the first load fires without the saved columns and
  // immediately runs again with them.
  useEffect(() => {
    if (!open || !ready) return;
    load();
    return () => abortRef.current?.abort();
  }, [open, ready, load]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const isPrepull = cell?.kind === 'prepull';

  const baseColumns = useMemo<ColumnDef<Row>[]>(() => {
    const text = (key: string, header: string): ColumnDef<Row> => ({
      accessorKey: key,
      header,
      filterFn: 'arrIncludes',
      cell: ({ getValue }) => (getValue<string>() || '-'),
    });
    const date = (key: string, header: string): ColumnDef<Row> => ({
      accessorKey: key,
      header,
      filterFn: 'arrIncludes',
      cell: ({ getValue }) => fmtDate(getValue<string>()),
    });

    const cols: ColumnDef<Row>[] = [
      {
        accessorKey: 'pro',
        header: 'Pro #',
        filterFn: 'arrIncludes',
        cell: ({ row }) => {
          const pro = row.original.pro;
          if (!canViewPro) return <span className="text-body">{pro}</span>;
          return (
            <button
              type="button"
              onClick={() => onProClick(pro)}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
            >
              {pro}
            </button>
          );
        },
      },
      // `lc` is this page's axis, not an orders-scope field, so it keeps its header.
      { ...text('lc', 'LC'), meta: { registryTitle: false } },
      text('customer', 'Customer'),
      text('container', 'Equipment #'),
      text('movetype', 'Move Type'),
      {
        accessorKey: 'schedule_status',
        header: 'Status',
        filterFn: 'arrIncludes',
        cell: ({ getValue }) => fmtStatus(getValue<string>()),
      },
      // The bucket's own proposed date first — it is what the cell was cut on —
      // then the other side of the pair, so the gap reads left to right.
      ...(isPrepull
        ? [date('proposed_prepull_date', 'Proposed Prepull'), date('cms_prepull_date', 'CMS Prepull')]
        : [date('proposed_appt_date', 'Proposed Delivery'), date('cms_delivery_appt', 'CMS Delivery Appt')]),
      ...(isPrepull
        ? [date('proposed_appt_date', 'Proposed Delivery')]
        : [date('proposed_prepull_date', 'Proposed Prepull')]),
      text('delivery_location', 'Delivery Location'),
      date('available', 'Available'),
      // The LC's own scheduling note, not the order's Comments field.
      { ...text('comments', 'Scheduling Comments'), meta: { registryTitle: false } },
    ];
    return cols;
  }, [canViewPro, onProClick, isPrepull]);

  const { columns, availableFields } = useAdditionalFieldColumns<Row>(
    fieldSelection, { baseColumns, coveredFields: FINALIZE_COVERED_FIELDS },
  );

  const groupableColumns = useMemo(
    () => ['lc', 'customer', 'movetype', 'schedule_status', 'delivery_location'],
    [],
  );

  const pros = useMemo(() => Array.from(new Set(rows.map((r) => r.pro))), [rows]);

  useAltHotkeys({ C: onClose }, undefined, { layer: open });
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open || !cell) return null;

  const table = cell.kind === 'prepull' ? 'Prepulls not finalized' : 'Deliveries not finalized';
  const who = cell.lcName ?? 'All LCs';
  const title = `${table} — ${who}, ${cell.label}`;

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-scrim/50 p-4" onClick={onClose}>
      <div
        ref={panelRef}
        style={panelStyle}
        className="relative flex flex-col w-full max-w-6xl h-[80vh] max-h-[90vh] bg-white dark:bg-gray-900 rounded-lg shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title={title}
          onClose={onClose}
          dragHandleProps={dragHandleProps}
          className="flex-shrink-0"
          actions={
            pros.length > 0 ? (
              <a
                href={`/searchresults?q=${pros.join(',')}&multi=true`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-sky-300 hover:text-white hover:underline whitespace-nowrap"
              >
                Open {pros.length} pro{pros.length === 1 ? '' : 's'} in search
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              </a>
            ) : undefined
          }
        />

        {truncated && (
          <div className="flex-shrink-0 px-3 pt-2 text-xs text-amber-600 dark:text-amber-400">
            Showing the first {rows.length.toLocaleString()} of {cell.count.toLocaleString()} —
            narrow the cell to see the rest.
          </div>
        )}

        <div className="flex-1 min-h-0 p-3">
          {error ? (
            <div className="flex h-full items-center justify-center text-red-600 dark:text-red-400">{error}</div>
          ) : (
            <TanTable
              data={rows}
              columns={columns}
              availableFields={availableFields}
              additionalFields={fieldSelection.additionalFields}
              onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
              trackViewedColumnId="pro"
              loading={loading}
              searchPlaceholder="Search…"
              enableColumnFilters
              enableSearch
              enableGrouping
              groupableColumns={groupableColumns}
              enableShowHideCols
              enableExcelExport
              fillContainer
              disableVirtualization
              defaultPageSize={200}
              settingsKey={SETTINGS_KEY}
              disableSettingsPersistence
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FinalizeDetailModal;
